import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Map } from 'immutable';
import { BASIC_POKEMON, ENERGY, STAGE_1, STAGE_2 } from '../constants';
import { selectActiveGame, selectActivePokemon, selectOptions } from '../ActiveGame/selectors';
import { evolveReason, evolveTargets, optionReason } from '../ActiveGame/options';
import { putActivePokemon } from '../GameStart/actions';
import { startAttachFlow, startBenchFlow, startEvolveFlow } from '../Pokemon/actions';
import ConfirmButton from '../ConfirmButton';
import TrainerMenu from '../Trainer/TrainerMenu';
import { Button, Muted } from '../Controls';
import { selectAttachFlowing, selectBenchFlowing, selectEvolveFlowing } from '../Pokemon/selectors';
import { selectPuttingActive } from '../GameStart/selectors';

/** One action for the card: a button, greyed out with the server's reason when it can't be done now. */
const Choice = ({ reason, children }) => (
  <>
    {children}
    {reason && <Muted>{reason}</Muted>}
  </>
);

Choice.propTypes = {
  reason: PropTypes.string,
  children: PropTypes.node.isRequired,
};

/**
 * What can be done with one card from your hand right now. Whether each action is allowed, and why not, comes
 * from the server's options, so the menu never works out the rules itself.
 */
const HandMenu = ({ card, userId }) => {
  const dispatch = useDispatch();
  const activePokemon = useSelector((state) => selectActivePokemon(state, { userId }));
  const attachFlowing = useSelector(selectAttachFlowing);
  const benchFlowing = useSelector(selectBenchFlowing);
  const evolveFlowing = useSelector(selectEvolveFlowing);
  const game = useSelector(selectActiveGame);
  const options = useSelector(selectOptions);
  const puttingActive = useSelector(selectPuttingActive);

  const cardTypes = card.get('cardTypes').map(cardType => cardType.get('id')).toArray();
  const position = card.get('order');
  const settingUp = !game.get('active_player_id');
  const isBasic = cardTypes.includes(BASIC_POKEMON);
  const isEvolution = cardTypes.includes(STAGE_1) || cardTypes.includes(STAGE_2);

  if (isBasic && settingUp && !activePokemon) {
    return (
      <ConfirmButton
        onConfirm={() => dispatch(putActivePokemon(position))}
        disabled={puttingActive}
        prompt={`Make ${card.get('name')} your active Pokémon?`}
        primary
      >
        Make active
      </ConfirmButton>
    );
  }

  if (isBasic) {
    const reason = optionReason(options && options.get('bench'));
    return (
      <Choice reason={reason}>
        <Button $primary onClick={() => dispatch(startBenchFlow(position))} disabled={!!reason || benchFlowing}>
          Put on bench
        </Button>
      </Choice>
    );
  }

  if (cardTypes.includes(ENERGY)) {
    const reason = optionReason(options && options.get('attach'));
    return (
      <Choice reason={reason}>
        <Button $primary onClick={() => dispatch(startAttachFlow(position))} disabled={!!reason || attachFlowing}>
          Attach to a Pokémon
        </Button>
      </Choice>
    );
  }

  if (isEvolution) {
    const targets = evolveTargets(options, position);
    const reason = targets.length ? null : (evolveReason(options, position) || optionReason(undefined));
    return (
      <Choice reason={reason}>
        <Button $primary onClick={() => dispatch(startEvolveFlow(position))} disabled={!!reason || evolveFlowing}>
          Evolve
        </Button>
      </Choice>
    );
  }

  return <TrainerMenu card={card} />;
};

HandMenu.propTypes = {
  card: PropTypes.instanceOf(Map).isRequired,
  userId: PropTypes.number.isRequired,
};

export default HandMenu;
